/**
 * Client-side CSV export utility.
 * Generates a UTF-8 BOM-prefixed CSV for correct Excel opening, then triggers
 * a browser download — no server round-trip required.
 */

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Fields containing comma, newline, or double-quote must be quoted.
  // Internal double-quotes are escaped by doubling them.
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * @param rows    Array of objects — each object is one CSV row.
 * @param filename  Download filename including .csv extension.
 * @param columns  Optional ordered column definitions. If omitted, keys of the
 *                 first row are used in insertion order.
 *                 Provide `{ key, label }` pairs so you can rename headers.
 */
export function exportCsv(
  rows: Record<string, unknown>[],
  filename: string,
  columns?: { key: string; label: string }[],
): void {
  if (rows.length === 0) return;

  const cols = columns ?? Object.keys(rows[0]).map((k) => ({ key: k, label: k }));

  const header = cols.map((c) => escapeCsvCell(c.label)).join(',');
  const body = rows
    .map((row) => cols.map((c) => escapeCsvCell(row[c.key])).join(','))
    .join('\r\n');

  // UTF-8 BOM (\uFEFF) ensures Excel opens the file with correct encoding
  const csv = '\uFEFF' + header + '\r\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
